// src/components/admin/MemeSubmissionsModerator.tsx

import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import { getUrl } from 'aws-amplify/storage';

const client = generateClient<Schema>();

interface MemeSubmission {
  userId: string;
  submissionDate: string;
  type: 'IMAGE' | 'TEXT';
  content: string;
  status: 'PENDING' | 'FEATURED' | 'REJECTED';
  originalFilename?: string;
  fileSize?: number;
  mimeType?: string;
  moderationNotes?: string;
  reviewedAt?: string;
  featuredDate?: string;
  createdAt?: string;
}

export default function MemeSubmissionsModerator() {
  const [submissions, setSubmissions] = useState<MemeSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'PENDING' | 'FEATURED' | 'REJECTED'>('PENDING');
  const [selectedSubmission, setSelectedSubmission] = useState<MemeSubmission | null>(null);
  const [notes, setNotes] = useState('');
  const [awarding, setAwarding] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, [filter]);

const fetchSubmissions = async () => {
  setLoading(true);
  try {
    const result = await client.graphql({
      query: `
        query ListMemeSubmissions($filter: ModelMemeSubmissionFilterInput) {
          listMemeSubmissions(filter: $filter) {
            items {
              userId
              submissionDate
              type
              content
              status
              originalFilename
              fileSize
              mimeType
              moderationNotes
              reviewedAt
              featuredDate
              createdAt
            }
          }
        }
      `,
      variables: {
        filter: {
          status: { eq: filter }
        }
      }
    }) as any;

    const items = result.data?.listMemeSubmissions?.items || [];

    // Convert S3 paths to signed URLs for images
    const submissionsWithUrls = await Promise.all(
      items.map(async (sub: MemeSubmission) => {
        if (sub.type === 'IMAGE') {
          const urlResult = await getUrl({ path: sub.content });
          return {
            ...sub,
            imageUrl: urlResult.url.toString()
          };
        }
        return sub;
      })
    );

    setSubmissions(submissionsWithUrls);
  } catch (error) {
    void ['Failed to fetch submissions:', error];
  } finally {
    setLoading(false);
  }
};

  const moderateSubmission = async (
    submission: MemeSubmission,
    newStatus: 'FEATURED' | 'REJECTED'
  ) => {
    try {
      await client.graphql({
        query: `
          mutation UpdateSubmission($input: UpdateMemeSubmissionInput!) {
            updateMemeSubmission(input: $input) {
              userId
              submissionDate
              status
            }
          }
        `,
        variables: {
          input: {
            userId: submission.userId,
            submissionDate: submission.submissionDate,
            status: newStatus,
            moderationNotes: notes,
            reviewedAt: new Date().toISOString(),
            ...(newStatus === 'FEATURED' && {
              featuredDate: new Date().toISOString().split('T')[0]
            })
          }
        }
      });

      alert(`✅ Submission ${newStatus.toLowerCase()}!`);
      setSelectedSubmission(null);
      setNotes('');
      fetchSubmissions();
    } catch (error) {
      void ['Failed to moderate:', error];
      alert('❌ Failed to update submission');
    }
  };

  const awardFreeYear = async (submission: MemeSubmission) => {
    if (!confirm(`Award 1 free year to user ${submission.userId}?`)) return;

    setAwarding(true);
    try {
      // TODO: Implement your upgrade logic here
      // This should:
      // 1. Update User.tier = "paid"
      // 2. Set User.planExpiresAt = now + 1 year
      // 3. Set User.pricePaidCents = 0 (or special value for contest win)

      await client.graphql({
        query: `
          mutation UpdateUser($input: UpdateUserInput!) {
            updateUser(input: $input) {
              id
              tier
            }
          }
        `,
        variables: {
          input: {
            id: submission.userId,
            tier: 'paid',
            // Contest winners get lifetime access. Sentinel value 2099 used for entitlement gate compatibility.
            planExpiresAt: '2099-12-31T23:59:59.000Z',
            pricePaidCents: 0 // Contest win - free
          }
        }
      });

      alert('✅ Free year awarded!');
    } catch (error) {
      void ['Failed to award year:', error];
      alert('❌ Failed to award free year');
    } finally {
      setAwarding(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading submissions...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-4">Meme Submissions Contest</h2>
        <p className="text-gray-600 mb-4">
          Review daily submissions and select winners. Featured users receive 1 free year!
        </p>

        {/* Filter Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('PENDING')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'PENDING'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            ⏳ Pending
          </button>
          <button
            onClick={() => setFilter('FEATURED')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'FEATURED'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            ⭐ Featured
          </button>
          <button
            onClick={() => setFilter('REJECTED')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'REJECTED'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            ❌ Rejected
          </button>
        </div>
      </div>

      {/* Submissions Grid */}
      {submissions.length === 0 ? (
        <div className="bg-white shadow-md rounded-lg p-12 text-center">
          <p className="text-gray-500 text-lg">No {filter.toLowerCase()} submissions yet!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {submissions.map((submission) => (
            <div
              key={`${submission.userId}-${submission.submissionDate}`}
              className="bg-white shadow-md rounded-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              {/* Content Preview */}
              <div className="bg-gray-50 p-4 min-h-[200px] flex items-center justify-center">
                {submission.type === 'IMAGE' ? (
                  <img
                    src={(submission as any).imageUrl}
                    alt="Submission"
                    className="max-h-48 max-w-full object-contain"
                  />
                ) : (
                  <div className="text-center p-4">
                    <p className="text-gray-700 italic">"{submission.content}"</p>
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {new Date(submission.createdAt || '').toLocaleDateString()}
                  </span>
                  <span
                    className={`px-2 py-1 rounded text-xs font-bold ${
                      submission.type === 'IMAGE'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}
                  >
                    {submission.type}
                  </span>
                </div>

                {submission.originalFilename && (
                  <p className="text-xs text-gray-500 truncate">
                    📎 {submission.originalFilename}
                  </p>
                )}

                {submission.moderationNotes && (
                  <div className="bg-gray-50 rounded p-2">
                    <p className="text-xs text-gray-600">{submission.moderationNotes}</p>
                  </div>
                )}

                {/* Actions */}
                {filter === 'PENDING' ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedSubmission(submission)}
                      className="flex-1 py-2 bg-brand text-white rounded-lg font-semibold hover:bg-branddark"
                    >
                      Review
                    </button>
                  </div>
                ) : filter === 'FEATURED' ? (
                  <button
                    onClick={() => awardFreeYear(submission)}
                    disabled={awarding}
                    className="w-full py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
                  >
                    🎁 Award Free Year
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h3 className="text-2xl font-bold">Review Submission</h3>

              {/* Content */}
              <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] flex items-center justify-center">
                {selectedSubmission.type === 'IMAGE' ? (
                  <img
                    src={(selectedSubmission as any).imageUrl}
                    alt="Submission"
                    className="max-h-96 max-w-full object-contain"
                  />
                ) : (
                  <p className="text-lg text-gray-700 italic">
                    "{selectedSubmission.content}"
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Moderation Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Great content! / Duplicate submission / etc."
                  className="w-full border border-gray-300 rounded-lg p-3"
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => moderateSubmission(selectedSubmission, 'FEATURED')}
                  className="flex-1 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700"
                >
                  ⭐ Feature (Win!)
                </button>
                <button
                  onClick={() => moderateSubmission(selectedSubmission, 'REJECTED')}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700"
                >
                  ❌ Reject
                </button>
                <button
                  onClick={() => {
                    setSelectedSubmission(null);
                    setNotes('');
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}